<template>
  <div class="post">
    <header class="post__nav">
      <div class="post__nav-inner">
        <router-link to="/" class="post__brand">
          <span class="post__brand-mark">S</span>
          <strong>Sauda</strong>
        </router-link>
        <div class="post__nav-actions">
          <LanguageSwitcher />
          <router-link to="/kassa-login" class="post__btn post__btn--ghost">{{ $t('public.common.login') }}</router-link>
          <router-link to="/register" class="post__btn post__btn--solid">{{ $t('public.common.startFree') }}</router-link>
        </div>
      </div>
    </header>

    <div v-if="post" class="post__wrap">
      <article class="post__article">
        <router-link to="/blog" class="post__back">
          <i class="mdi mdi-arrow-left"></i>
          {{ $t('public.common.allArticles') }}
        </router-link>

        <span class="post__category">{{ post.category }}</span>
        <h1>{{ post.title }}</h1>
        <div class="post__meta">
          <span><i class="mdi mdi-clock-outline"></i> {{ post.readMinutes }} {{ $t('public.common.minutes') }}</span>
        </div>

        <div class="post__body">
          <p v-for="(paragraph, index) in post.body" :key="index">{{ paragraph }}</p>
        </div>

        <div v-if="post.takeaway" class="post__takeaway">
          <i class="mdi mdi-lightbulb-on-outline"></i>
          <p>{{ post.takeaway }}</p>
        </div>
      </article>

      <section class="post__cta">
        <div class="post__cta-inner">
          <h2>{{ $t('public.post.tryTitle') }}</h2>
          <p>{{ $t('public.post.tryText') }}</p>
          <router-link to="/register" class="post__btn post__btn--big post__btn--light">
            {{ $t('public.common.startFree') }}
            <i class="mdi mdi-arrow-right"></i>
          </router-link>
        </div>
      </section>
    </div>

    <div v-else class="post__missing">
      <i class="mdi mdi-file-search-outline"></i>
      <h1>{{ $t('public.post.notFound') }}</h1>
      <p>{{ $t('public.post.notFoundText') }}</p>
      <router-link to="/blog" class="post__btn post__btn--solid">
        {{ $t('public.post.toArticles') }}
      </router-link>
    </div>

    <footer class="post__footer">
      <router-link to="/" class="post__brand">
        <span class="post__brand-mark">S</span>
        <strong>Sauda</strong>
      </router-link>
      <nav class="post__footer-links">
        <router-link to="/">{{ $t('public.common.backHome') }}</router-link>
        <router-link to="/blog">{{ $t('public.common.blog') }}</router-link>
        <router-link to="/register">{{ $t('public.common.startFree') }}</router-link>
      </nav>
      <p>{{ $t('public.common.footer', { year: new Date().getFullYear() }) }}</p>
    </footer>
  </div>
</template>

<script>
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { getPostBySlug } from '@/data/blogPosts'

export default {
  name: 'BlogPost',
  components: { LanguageSwitcher },
  computed: {
    post() {
      return getPostBySlug(this.$route.params.slug, this.$i18n.locale)
    },
  },
}
</script>

<style scoped lang="scss">
$ink: #152033;
$muted: #6b7a90;
$accent: #0b4f8a;
$dark: #10243a;

.post {
  min-height: 100vh;
  background: #f6f9fc;
  color: $ink;
  font-size: 16px;
  overflow-x: hidden;
}

/* ---------- Топбар ---------- */
.post__nav {
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e3ebf3;
}

.post__nav-inner {
  max-width: 1160px;
  margin: 0 auto;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.post__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: $ink;

  strong {
    font-size: 19px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }
}

.post__brand-mark {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #163a52, $accent);
  color: #fff;
  font-weight: 800;
  font-size: 18px;
}

.post__nav-actions {
  display: flex;
  gap: 10px;
}

/* ---------- Кнопки ---------- */
.post__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border: 0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.18s ease;

  &--solid {
    background: $accent;
    color: #fff;

    &:hover {
      background: #093f6d;
    }
  }

  &--ghost {
    background: transparent;
    color: $ink;

    &:hover {
      background: #e9f1f8;
    }
  }

  &--big {
    padding: 15px 26px;
    font-size: 16px;
    border-radius: 14px;

    .mdi {
      font-size: 18px;
      transition: transform 0.18s ease;
    }

    &:hover .mdi {
      transform: translateX(4px);
    }
  }

  &--light {
    background: #fff;
    color: $accent;

    &:hover {
      background: #eef4fa;
      transform: translateY(-1px);
    }
  }
}

/* ---------- Статья ---------- */
.post__wrap {
  max-width: 720px;
  margin: 0 auto;
  padding: 48px 20px 24px;
}

.post__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 22px;
  color: $muted;
  font-size: 14px;
  font-weight: 650;
  text-decoration: none;
  transition: color 0.18s ease;

  &:hover {
    color: $accent;
  }

  .mdi {
    font-size: 16px;
  }
}

.post__category {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 999px;
  background: #e9f1f8;
  color: $accent;
  font-size: 12.5px;
  font-weight: 700;
  margin-bottom: 14px;
}

.post__article h1 {
  margin: 0 0 14px;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.post__meta {
  display: flex;
  gap: 16px;
  margin-bottom: 30px;
  color: $muted;
  font-size: 13.5px;

  .mdi {
    font-size: 15px;
    vertical-align: -1px;
  }
}

.post__body {
  font-size: 17px;
  line-height: 1.85;
  color: #2b3a4d;

  p {
    margin: 0 0 20px;
  }
}

.post__takeaway {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 32px;
  padding: 20px 22px;
  border-radius: 16px;
  background: #eef4fa;
  border-left: 3px solid $accent;

  .mdi {
    font-size: 22px;
    color: $accent;
    flex-shrink: 0;
    margin-top: 1px;
  }

  p {
    margin: 0;
    font-size: 15px;
    font-weight: 650;
    line-height: 1.55;
    color: $ink;
  }
}

/* ---------- Не найдено ---------- */
.post__missing {
  max-width: 480px;
  margin: 0 auto;
  padding: 100px 20px;
  text-align: center;

  .mdi {
    font-size: 52px;
    color: #bcd2e4;
  }

  h1 {
    margin: 18px 0 8px;
    font-size: 26px;
    font-weight: 800;
  }

  p {
    margin: 0 0 26px;
    color: $muted;
  }
}

/* ---------- CTA ---------- */
.post__cta {
  max-width: 1160px;
  margin: 0 auto;
  padding: 32px 20px 72px;
}

.post__cta-inner {
  padding: 52px 32px;
  border-radius: 26px;
  text-align: center;
  color: #fff;
  background:
    radial-gradient(circle at 15% 20%, rgba(90, 168, 220, 0.3), transparent 40%),
    linear-gradient(135deg, #143252, $dark);

  h2 {
    margin: 0 0 10px;
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0 auto 26px;
    max-width: 520px;
    opacity: 0.82;
    font-size: 15.5px;
  }
}

/* ---------- Футер ---------- */
.post__footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 28px 20px 36px;
  border-top: 1px solid #e3ebf3;

  p {
    margin: 0;
    color: $muted;
    font-size: 13px;
    text-align: center;
  }
}

.post__footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 18px;

  a {
    font-size: 13.5px;
    font-weight: 650;
    color: $muted;
    text-decoration: none;
    transition: color 0.18s ease;

    &:hover {
      color: $accent;
    }
  }
}

/* ---------- Адаптив ---------- */
@media (max-width: 600px) {
  .post__nav-inner {
    padding: 10px 14px;
  }

  .post__btn--ghost {
    display: none;
  }

  .post__article h1 {
    font-size: 26px;
  }

  .post__body {
    font-size: 16px;
  }

  .post__cta-inner {
    padding: 38px 20px;

    h2 {
      font-size: 22px;
    }
  }
}
</style>
